/**
 * Workspace Resolver Hook
 * Central hook for resolving and managing current workspace
 * This is the SINGLE SOURCE OF TRUTH for workspace_id
 */

import { useEffect, useState, useRef } from 'react';
import { useAuth } from '@/lib/auth';
import { WorkspacesService } from '@/data/workspaces.service';
import { ProfilesService } from '@/data/profiles.service';
import { useWorkspaceScope } from '@/contexts/useWorkspaceScope';
import { logger } from '@/lib/logger';
import { handleError } from '@/lib/errorHandler';

export interface WorkspaceResolverResult {
  workspaceId: string | null;
  isLoading: boolean;
  error: Error | null;
}

/**
 * Resolves the current workspace for the authenticated user
 * Priority:
 * 1. activeWorkspaceId from WorkspaceScopeContext (if set)
 * 2. workspace_id from profiles table
 * 3. First workspace from workspace_members
 */
export function useWorkspaceResolver(): WorkspaceResolverResult {
  const { user, loading: authLoading } = useAuth();
  const { activeWorkspaceId, setActiveWorkspace } = useWorkspaceScope();
  
  // Wrapper for setActiveWorkspace to match old API
  const setActiveWorkspaceId = (workspaceId: string) => {
    setActiveWorkspace(workspaceId);
  };
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const resolvedRef = useRef(false);

  useEffect(() => {
    if (authLoading) {
      setIsLoading(true);
      return;
    }

    if (!user) {
      setIsLoading(false);
      setError(null);
      resolvedRef.current = false;
      return;
    }

    // If we already have activeWorkspaceId, use it
    if (activeWorkspaceId) {
      setIsLoading(false);
      setError(null);
      return;
    }

    // Resolve workspace
    const resolveWorkspace = async () => {
      if (resolvedRef.current) return;
      resolvedRef.current = true;

      try {
        setIsLoading(true);
        setError(null);

        logger.action('resolveWorkspace', { userId: user.id });

        // 1. Try to get from profile
        const profile = await ProfilesService.getProfile(user.id);
        if (profile?.workspace_id) {
          logger.action('workspaceResolved', { 
            userId: user.id, 
            workspaceId: profile.workspace_id,
            source: 'profile' 
          });
          setActiveWorkspaceId(profile.workspace_id);
          setIsLoading(false);
          return;
        }

        // 2. Try to get from workspace_members
        const workspaceId = await WorkspacesService.getCurrentWorkspaceId(user.id);
        if (workspaceId) {
          logger.action('workspaceResolved', { 
            userId: user.id, 
            workspaceId,
            source: 'workspace_members' 
          });
          setActiveWorkspaceId(workspaceId);
          setIsLoading(false);
          return;
        }

        // 3. No workspace found - this is OK, user might need onboarding
        logger.action('workspaceNotFound', { userId: user.id });
        setIsLoading(false);
        setError(null);
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to resolve workspace');
        handleError(error, {
          action: 'resolveWorkspace',
          component: 'useWorkspaceResolver',
          userId: user.id,
        }, false); // Don't show toast for workspace resolution errors
        setError(error);
        setIsLoading(false);
        resolvedRef.current = false;
      }
    };

    resolveWorkspace();
  }, [user, authLoading, activeWorkspaceId, setActiveWorkspaceId]);

  return {
    workspaceId: activeWorkspaceId,
    isLoading: isLoading || authLoading,
    error,
  };
}

